<template>
  <BaseModal
    :heading="t('global.message')"
    small
    useForm
    @submit="submit"
    @close="emit('close')"
  >
    <Flex direction="column">
      <BaseAlert
        v-if="selectedValue === 'email' && !customerId"
        color="warning"
        :text="t('customers.bulk_warning')"
        v-test="'emailBaseAlert'"
      />
      <BaseAlert
        v-if="noCreditsForBulkMessage"
        color="warning"
        :text="t('messages_sms.no_credits_warning')"
      />
      <BaseRadio
        v-model="selectedValue"
        inline
        label="type"
        :options="[
          {
            label: t('global.email'),
            value: 'email'
          },
          {
            label: t('global.sms_message'),
            value: 'sms'
          }
        ]"
        v-test="'customer-message-type'"
      />
      <div v-if="noMobile || noEmail">
        <BaseAlert
          v-if="noMobile"
          color="error"
          :text="$t('customers.no_mobile_phone') + customerName"
          :primaryAction="{
            text: $t('global.actions.edit_customer'),
            routerLink: {
              name: 'customers-edit',
              params: { id: customerId }
            }
          }"
          v-test="'error-no-mobile'"
        />
        <BaseAlert
          v-if="noEmail"
          color="error"
          :text="$t('customers.no_email') + customerName"
          :primaryAction="{
            text: $t('global.actions.edit_customer'),
            routerLink: {
              name: 'customers-edit',
              params: { id: customerId }
            }
          }"
          v-test="'error-no-email'"
        />
      </div>
      <BaseSpinner v-if="loadingTemplate" />
      <Flex
        direction="column"
        v-else-if="selectedValue === 'email'"
      >
        <BaseInput
          v-model="subject"
          :label="t('email_templates.subject')"
          :required="selectedValue === 'email'"
          v-test="'customersSendEmailSubject'"
        />
        <BaseEditor
          v-model="emailMessage"
          :label="t('global.message')"
          :required="selectedValue === 'email'"
          :cloudinaryName="UploadedImageUsage.NewsletterContent"
          :disabled="noEmail && selectedValue === 'email'"
          v-test="'customersSendEmailMessage'"
        />
      </Flex>

      <BaseSlider
        v-else
        :step="purchaseCreditsView"
      >
        <template #1>
          <Flex justify="end">
            <Flex
              align="center"
              :gap=".5"
            >
              <BaseLabel
                :color="creditsColor"
                v-test="'credit-balance'"
              >
                {{ t('sms_credits.credits_left', { amount: currentBalance }) }}
              </BaseLabel>
              <BaseText
                size="s"
                iconAfter="shopping-cart"
                :routerLink="{ name: 'messages-purchase-credits' }"
                v-test="'credits-link'"
              >
                {{ t('admin.dashboard.credits.add_credits') }}
              </BaseText>
            </Flex>
          </Flex>
          <div :class="$style.smsMessage">
            <BaseInput
              v-model="smsMessage"
              :label="t('global.message')"
              type="textarea"
              :rows="6"
              :maxLength="hasUnicode ? 320 : 640"
              :required="selectedValue === 'sms'"
              :disabled="noMobile && selectedValue === 'sms'"
              v-test="'customersSendSmsMessage'"
            />
          </div>
          <BaseGrid container>
            <BaseGrid>
              <BaseText inline>{{ characters }}/ </BaseText>
              <BaseText
                inline
                bold
              >
                {{ nextMessageCharacters }}
                {{ maxCharacters }}
              </BaseText>
            </BaseGrid>
            <BaseGrid
              alignRight
              v-test="'customerSendSmsCredits'"
            >
              <Flex :gap=".5">
                <BaseLabel color="success">
                  {{ credits }}
                  {{ t('global.credits') }}
                </BaseLabel>
              </Flex>
            </BaseGrid>
          </BaseGrid>

          <BaseText
            v-if="hasUnicode"
            color="secondary"
            size="s"
            v-test="'sendMessageEmoji'"
          >
            * {{ t('message_modal.emojis') }}
          </BaseText>
        </template>

        <template #2>
          <PurchaseCredits
            @onBack="purchaseCreditsView = 1"
            @update:amount="(amount: number) => { chosenAmount = amount }"
          />
        </template>
      </BaseSlider>
    </Flex>

    <template
      v-if="purchaseCreditsView === 2"
      #footerSub
    >
      <BaseText @click="purchaseCreditsView = 1">
        {{ t('global.back') }}
      </BaseText>
    </template>

    <template #footer>
      <BaseButton
        color="inverted"
        @click="cancel"
      >
        {{ t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        :disabled="noMobile || noEmail || noCreditsForBulkMessage"
        :loading="isLoading"
        submitForm
        v-test="'customersSendMessageSave'"
      >
        {{ purchaseCreditsView === 1 ? t('global.actions.send') : t('sms_credits.button_submit') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { computed, inject, onMounted, type Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { flash, modal } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { UploadedImageUsage } from '@/types';
import PurchaseCredits from './PurchaseCredits.vue';
import {
  CREATE_CUSTOM_MESSAGE,
  GET_MESSAGING_EMAIL_TEMPLATE,
  SEND_MESSAGE_TO_CUSTOMERS_SEGMENT,
} from './segments/graphql';

// Props definition
interface Props {
  customerData?: {
    customerId?: string;
    customerMobile?: string;
    customerEmail?: string;
    customerName?: string;
  };
  searchQuery?: string;
  filters?: any[];
  selectedMessageType: string;
  segmentKey?: string;
}
const props = withDefaults(defineProps<Props>(), {
  selectedMessageType: 'email',
  filters: () => [],
});

// Emits definition
const emit = defineEmits(['close']);

// Inject dependencies
const selectedCustomers = inject<Ref<number[]>>('selectedCustomers', ref([]));
const totalCount = inject<Ref<number>>('totalCount', ref(0));

// Composables
const { t } = useI18n();
const { company } = useCompanyStore();

// Reactive state
const messageCharacters = ref(160);
const customerId = ref<string | null>(null);
const query = ref('');
const mobilePhone = ref<string | null>(null);
const customerName = ref<string | null>(null);
const email = ref<string | null>(null);
const isLoading = ref(false);
const currentBalance = ref(company.smsCredits || 0);
const loadingTemplate = ref(false);
const subject = ref('');
const emailMessage = ref('');
const smsMessage = ref('');
const selectedValue = ref(props.selectedMessageType);
const subjectChanged = ref(false);
const messageChanged = ref(false);
const chosenAmount = ref(0);
const purchaseCreditsView = ref(1);

// Computed properties
const message = computed(() =>
  selectedValue.value === 'email' ? emailMessage.value : smsMessage.value,
);

const noEmail = computed(() => {
  return !!customerId.value && selectedValue.value === 'email' && !email.value;
});

const noMobile = computed(() => {
  return (
    !!customerId.value && selectedValue.value === 'sms' && !mobilePhone.value
  );
});

const characters = computed(() => {
  return smsMessage.value.length || 0;
});

const nextMessageCharacters = computed(() => {
  return messageCharacters.value * credits.value;
});

const hasUnicode = computed(() => {
  for (let i = 0; i < smsMessage.value.length; i++) {
    if (smsMessage.value.charCodeAt(i) > 255) {
      return true;
    }
  }
  return false;
});

const maxCharacters = computed(() => {
  if (nextMessageCharacters.value > 160 && hasUnicode.value) {
    return '(max)';
  }
  return (nextMessageCharacters.value > 480 && '(max)') || '';
});

const credits = computed(() => {
  return Math.max(1, Math.ceil(characters.value / messageCharacters.value));
});

const notEnoughCredits = computed(() => {
  return currentBalance.value < credits.value;
});

const noCreditsForBulkMessage = computed(() => {
  return (
    !currentBalance.value &&
    selectedValue.value === 'sms' &&
    totalCustomers.value > 0
  );
});

const creditsColor = computed(() => {
  if (currentBalance.value >= credits.value && currentBalance.value !== 0) {
    return 'success';
  } else if (currentBalance.value === 0) {
    return 'error';
  } else {
    return 'warning';
  }
});

// Methods
const fetchTemplate = () => {
  loadingTemplate.value = true;
  const { onResult } = useQuery(GET_MESSAGING_EMAIL_TEMPLATE, {
    id: `${props.segmentKey}_${selectedValue.value}`,
  });

  onResult(({ data: { messagingEmailTemplate } }) => {
    subject.value = messagingEmailTemplate.subject;
    loadingTemplate.value = false;

    if (selectedValue.value === 'email') {
      emailMessage.value = messagingEmailTemplate.content || '';
    } else {
      smsMessage.value = messagingEmailTemplate.content || '';
    }

    watch(subject, () => {
      subjectChanged.value = true;
    });

    watch(message, () => {
      messageChanged.value = true;
    });
  });
};

const cancel = () => {
  emit('close');
};

const send = () => {
  const { mutate } = props.segmentKey
    ? useMutation(SEND_MESSAGE_TO_CUSTOMERS_SEGMENT)
    : useMutation(CREATE_CUSTOM_MESSAGE);

  isLoading.value = true;

  mutate({
    input: props.segmentKey
      ? {
          segmentName: props.segmentKey,
          gateway: selectedValue.value.toUpperCase(),
          message: message.value,
          subject: subject.value || null,
        }
      : {
          customerId: customerId.value,
          customerIds: selectedCustomers.value,
          filters: props.filters,
          gateway: selectedValue.value.toUpperCase(),
          message: message.value,
          query: query.value,
          subject: subject.value || null,
        },
  })
    .then(() => {
      flash(t('global.flash.message_sent'));
      emit('close');
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const checkForPurchaseCredits = () => {
  const { mutate, onDone } = useMutation(
    gql`
      mutation createSmsCheckoutSession($input: CreateSmsCheckoutSessionInput!) {
        createSmsCheckoutSession(input: $input) {
          url
          errors {
            message
          }
        }
      }
    `,
    () => ({
      variables: {
        input: {
          amount: chosenAmount.value,
        },
      },
    }),
  );
  mutate();
  isLoading.value = true;
  onDone(
    ({
      data: {
        createSmsCheckoutSession: { url },
      },
    }) => {
      if (url) {
        window.location.href = url;
      } else {
        isLoading.value = false;
      }
    },
  );
};

const changeToPurchaseCreditsStep = () => {
  purchaseCreditsView.value = 2;
};

const totalCustomers = computed(() => {
  return selectedCustomers.value?.length || totalCount.value;
});

const submit = () => {
  if (purchaseCreditsView.value === 2) {
    checkForPurchaseCredits();
  } else if (
    selectedValue.value === 'sms' &&
    notEnoughCredits.value &&
    !totalCustomers.value
  ) {
    changeToPurchaseCreditsStep();
  } else if (customerId.value) {
    send();
  } else {
    modal('confirmation', {
      message: t(`global.confirmation.confirm_${selectedValue.value}_amount`, {
        item: `${totalCustomers.value} ${t('global.items.customer', totalCustomers.value)}`,
      }),
    }).then(() => {
      send();
    });
  }
};

// Watchers
if (props.segmentKey) {
  watch(
    selectedValue,
    () => {
      fetchTemplate();
    },
    {
      immediate: true,
    },
  );
}

// Lifecycle
onMounted(() => {
  if (props.customerData?.customerId) {
    customerId.value = props.customerData.customerId;
  }
  if (props.customerData?.customerMobile) {
    mobilePhone.value = props.customerData.customerMobile;
  }
  if (props.customerData?.customerEmail) {
    email.value = props.customerData.customerEmail;
  }
  if (props.customerData?.customerName) {
    customerName.value = props.customerData.customerName;
  }
  if (props.searchQuery) {
    query.value = props.searchQuery;
  }
});
</script>

<style
  lang="scss"
  module
>
.smsMessage {
  margin-bottom: $spacing;
}
</style>
